import { Link } from "react-router-dom";
import { LifeBuoy, Mail, Package, Timer } from "lucide-react";
import SEO from "../components/SEO";
import { Reveal, SectionTag } from "../components/bits";
import { BUSINESS } from "../data/business";

const CARDS = [
  { icon: Mail, title: "Email us", text: "The fastest way to reach a real human. Tell us your room size and goals and we'll point you at the right light.", href: `mailto:${BUSINESS.email}`, label: BUSINESS.email },
  { icon: Package, title: "Order questions", text: "Include your order number and we'll sort it — tracking, address changes or returns.", href: `mailto:${BUSINESS.email}?subject=Order%20question`, label: "Email about an order" },
  { icon: LifeBuoy, title: "Grow advice", text: "Hanging distances, spectrum and watering under LED — most answers are already in our free grow guides.", href: "/guides", label: "Browse grow guides", internal: true },
];

export default function Contact() {
  return (
    <div className="mx-auto max-w-7xl px-5 pb-28 pt-28 sm:px-8" data-testid="contact-page">
      <SEO
        title="Contact SuperTechLED — Grow Light Help & Order Support"
        description="Get in touch with SuperTechLED: order support, warranty help and friendly grow advice. Email supertechled@outlook.com — we reply within one business day."
      />
      <Reveal>
        <SectionTag>Talk to us</SectionTag>
        <h1 className="font-display mt-4 text-3xl font-bold tracking-tight text-[#F0FDF4] sm:text-4xl lg:text-5xl">
          We answer like growers, not a call centre
        </h1>
        <p className="mt-5 max-w-2xl text-sm leading-relaxed text-[#9FB8A8] sm:text-base">
          Whether it's picking the right fixture for your tent or tracking an order, flick us an email —
          <span className="text-[#86EFAC]">{` ${BUSINESS.email}`}</span> — and we'll get back to you within one business day.
        </p>
      </Reveal>

      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {CARDS.map((c, i) => (
          <Reveal key={c.title} delay={i * 0.08}>
            <div className="flex h-full flex-col rounded-3xl border border-[#39FF14]/15 bg-[#071009] p-8">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#39FF14]/25 bg-[#0A150F]">
                <c.icon className="h-5 w-5 text-[#39FF14]" />
              </span>
              <h2 className="font-display mt-5 text-lg font-bold text-[#F0FDF4]">{c.title}</h2>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-[#9FB8A8]">{c.text}</p>
              {c.internal ? (
                <Link to={c.href} data-testid={`contact-card-link-${i}`} className="mt-5 inline-flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#39FF14] hover:text-[#86EFAC]">
                  {c.label}
                </Link>
              ) : (
                <a href={c.href} data-testid={`contact-card-link-${i}`} className="mt-5 inline-flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#39FF14] hover:text-[#86EFAC]">
                  {c.label}
                </a>
              )}
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-14 flex items-center gap-3 rounded-2xl border border-[#39FF14]/10 bg-[#0A150F]/60 px-6 py-4">
        <Timer className="h-5 w-5 shrink-0 text-[#39FF14]" />
        <p className="text-sm text-[#9FB8A8]">
          We reply within one business day, usually much faster. For anything urgent about a delivery, put your order number in the subject line.
        </p>
      </Reveal>

      <Reveal className="mt-6 rounded-2xl border border-[#39FF14]/10 bg-[#0A150F]/60 px-6 py-5" data-testid="contact-business-details">
        <div className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#4ADE80]">Business details</div>
        <div className="mt-3 space-y-1.5 text-sm text-[#C9DCD0]">
          <p>{`${BUSINESS.name} · ${BUSINESS.address}`}</p>
          {BUSINESS.abn && <p>{`ABN ${BUSINESS.abn}`}</p>}
          {BUSINESS.phone && (
            <p>
              {`Phone: `}
              <a href={`tel:${BUSINESS.phone.replace(/\s/g, "")}`} className="text-[#39FF14] hover:text-[#86EFAC]">{BUSINESS.phone}</a>
            </p>
          )}
          <p>
            {`Email: `}
            <a href={`mailto:${BUSINESS.email}`} className="text-[#39FF14] hover:text-[#86EFAC]">{BUSINESS.email}</a>
          </p>
        </div>
      </Reveal>
    </div>
  );
}
